import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import {BaseComponent} from '../../base.component';
import {FactorComponent} from '../dialog/factor/factor.component';
import {ListResponse} from '../../../network/service/list.response';
import {SnackBarUtil} from '../../../util/snack.bar.util';
import {StringUtil} from '../../../util/string.util';
import {RequestType} from '../../../enums/request.type';
import {RegisterCommentComponent} from '../dialog/register-comment/register-comment.component';
import {RouteUtil} from '../../../util/route.util';
import {FactorStatus, FactorStatusHelper} from '../../../enums/factor.status';
import {Config} from '../../../common/config';

@Component({
  selector: 'app-orders',
  standalone: false,
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.scss'
})
export class OrdersComponent extends BaseComponent implements OnInit, OnDestroy {
  number: number = 0;
  isLoadMore: boolean = true;
  isMobile: boolean;
  factors?: any;
  count?: number = 0;
  activeOrders: any[] = [];
  isActiveOrdersLoading: boolean = true;
  isPreviousOrdersLoading: boolean = true;
  activeOrdersLoadedOnce: boolean = false;
  previousOrdersLoadedOnce: boolean = false;
  activeOrdersSkeletonPlaceholders: number[] = Array.from({length: 1}, (_, index) => index);
  previousOrdersSkeletonPlaceholders: number[] = Array.from({length: 2}, (_, index) => index);
  protected readonly StringUtil = StringUtil;
  private activeOrdersInterval?: any;

  constructor(injector: Injector) {
    super(injector);
    this.isMobile = window.innerWidth <= 992;
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  override loadOnline() {
    super.loadOnline();
    if (!this.previousOrdersLoadedOnce) {
      this.isPreviousOrdersLoading = true;
    }
    if (!this.isLoadMore) {
      return;
    }
    this.loadActiveOrder();
    this.serviceApi.getMyFactors(this.tableProperties.offset, undefined, 'Business,PaymentMethod,DeliveryMethod,UserAddress,FactorOrders,FactorOrders.Order,FactorOrders.Order.ServicePrice,FactorOrders.Order.ServicePrice.Service,FactorOrders.Order.OrderServicePriceAdditives,FactorOrders.Order.OrderServicePriceAdditives.ServicePriceAdditive,FactorOrders.Order.OrderServicePriceAdditives.ServicePriceAdditive.Attribute', RequestType.PAGINATE, {
      Filter: {
        Values: [
          {
            Field: 'OpenFactors',
            Type: 'EQ',
            Value: false
          }
        ]
      },
      Order: {
        Field: 'ModifyDate',
        Type: 'DESC'
      }
    }, Config.branch.BusinessId).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        this.factors = res.data;
        if ((res.data?.length ?? 0) < this.tableProperties.limit) {
          this.isLoadMore = false;
        } else {
          this.tableProperties.offset++;
        }
        this.factors = res.data;
        this.count = res.count;
        this.previousOrdersLoadedOnce = true;
        this.isPreviousOrdersLoading = false;
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
        this.isPreviousOrdersLoading = false;
      }
    }, () => {
      this.isPreviousOrdersLoading = false;
    });
  }

  loadActiveOrder() {
    const hasExistingActiveOrders = (this.activeOrders?.length ?? 0) > 0;
    this.isActiveOrdersLoading = hasExistingActiveOrders;
    if (this.hasAccount()) {
      this.serviceApi.getMyFactors(this.tableProperties.offset, undefined, 'Business,FactorOrders,FactorOrders.Order,FactorOrders.Order.ServicePrice,FactorOrders.Order.ServicePrice.Service,FactorOrders.Order.ServicePrice.Service.Images,PaymentMethod,DeliveryMethod,UserAddress,Metadata', RequestType.ALL, {
        Filter: {
          Values: [
            {
              Field: 'OpenFactors',
              Type: 'EQ',
              Value: true
            }
          ]
        },
        Order: {
          Field: 'ModifyDate',
          Type: 'DESC'
        }
      }, Config.branch.BusinessId).subscribe((res: ListResponse<any>) => {
        if (res.success && res.data && res.data.length > 0) {
          this.activeOrders = res.data;
          this.startActiveOrdersRefresh();
        } else {
          this.activeOrders = [];
          this.stopActiveOrdersRefresh();
        }
        this.activeOrdersLoadedOnce = true;
        this.isActiveOrdersLoading = false;
      }, () => {
        this.activeOrders = [];
        this.activeOrdersLoadedOnce = true;
        this.isActiveOrdersLoading = false;
        this.stopActiveOrdersRefresh();
      });
    } else {
      this.activeOrders = [];
      this.activeOrdersLoadedOnce = true;
      this.isActiveOrdersLoading = false;
      this.stopActiveOrdersRefresh();
    }
  }

  startActiveOrdersRefresh() {
    // پاک کردن interval قبلی اگر وجود داشته باشد
    this.stopActiveOrdersRefresh();
    // ایجاد interval جدید برای به‌روزرسانی هر 60 ثانیه (60000 میلی‌ثانیه)
    this.activeOrdersInterval = setInterval(() => {
      this.loadActiveOrder();
    }, 60000);
  }

  stopActiveOrdersRefresh() {
    if (this.activeOrdersInterval) {
      clearInterval(this.activeOrdersInterval);
      this.activeOrdersInterval = undefined;
    }
  }

  ngOnDestroy() {
    this.stopActiveOrdersRefresh();
  }

  onDeleteClicked() {
    if (this.number > 0) {
      this.number--;
    }
  }

  getFactorStatus(status: any) {
    const statusKey = FactorStatusHelper.getStatusText(status);
    return this.translateService.instant(statusKey);
  }

  getActiveOrderStatusMessage(factor: any): string {
    const statusKey = FactorStatusHelper.getStatusText(factor?.Status);
    if (statusKey === 'PROGRESSING') {
      return this.translateService.instant('OrderAwaitingStoreConfirmation');
    }
    return this.translateService.instant(statusKey);
  }

  getActiveOrderSecondaryMessage(factor: any): string {
    switch (factor?.Status?.toUpperCase()) {
      case 'PROGRESSING':
        return this.translateService.instant('ThenPreparationProcessWillBegin');
      default:
        return '';
    }
  }

  shouldShowProgress(factor: any): boolean {
    const status = factor?.Status?.toUpperCase();
    return status !== FactorStatus.CANCELED && status !== FactorStatus.CANCELED_BY_SYSTEM;
  }

  getProgressStage(factor: any): {
    icon1: boolean,
    icon2: boolean,
    icon3: boolean,
    dash1Percent: number,
    dash2Percent: number
  } {
    const status = factor?.Status?.toUpperCase();
    switch (status) {
      case FactorStatus.SUBMIT:
      case FactorStatus.PENDING_PAYMENT:
      case FactorStatus.PROGRESSING:
        // همه روی خط اول، همه خاکستری - دقیقاً مثل عکس
        return {
          icon1: false,
          icon2: false,
          icon3: false,
          dash1Percent: 0,
          dash2Percent: 0
        };
      case FactorStatus.PREPARATION:
        // خط اول تا نصفه پر - آیکون 1 و 2 فعال (دوچرخه، نه موتور)
        return {
          icon1: true,
          icon2: true,
          icon3: false,
          dash1Percent: 50,
          dash2Percent: 0
        };
      case FactorStatus.PROCESSING_CENTER:
        // خط اول پر، خط دوم کمی پر - آیکون 1 و 2 فعال (دوچرخه، نه موتور)
        return {
          icon1: true,
          icon2: true,
          icon3: false,
          dash1Percent: 100,
          dash2Percent: 30
        };
      case FactorStatus.DELIVERY_DISPATCHER:
        // آیکون 1 و 2 فعال، خط اول پر، خط دوم تا نصف پر - موتور فعال می‌شود
        return {
          icon1: true,
          icon2: true,
          icon3: false,
          dash1Percent: 100,
          dash2Percent: 50
        };
      case FactorStatus.DELIVERED:
        // همه فعال
        return {
          icon1: true,
          icon2: true,
          icon3: true,
          dash1Percent: 100,
          dash2Percent: 100
        };
      case FactorStatus.CANCELED:
      case FactorStatus.CANCELED_BY_SYSTEM:
        // هیچ پروگرسی نشان نده
        return {
          icon1: false,
          icon2: false,
          icon3: false,
          dash1Percent: 0,
          dash2Percent: 0
        };
      default:
        return {
          icon1: false,
          icon2: false,
          icon3: false,
          dash1Percent: 0,
          dash2Percent: 0
        };
    }
  }

  shouldShowMotorcycleIcon(factor: any): boolean {
    const status = factor?.Status?.toUpperCase();
    // موتور فقط در DELIVERY_DISPATCHER نمایش داده می‌شود
    return status === FactorStatus.DELIVERY_DISPATCHER;
  }

  getIconColor(factor: any, iconNumber: number): string {
    if (!this.shouldShowProgress(factor)) {
      return '#D0D0D0'; // Gray for cancelled
    }
    const progress = this.getProgressStage(factor);
    const activeColor = '#D68240';
    const inactiveColor = '#D0D0D0';
    if (iconNumber === 1) {
      return progress.icon1 ? activeColor : inactiveColor;
    } else if (iconNumber === 2) {
      return progress.icon2 ? activeColor : inactiveColor;
    } else if (iconNumber === 3) {
      return progress.icon3 ? activeColor : inactiveColor;
    }
    return inactiveColor;
  }

  getDeliveryMethodText(factor: any): string {
    if (factor?.DeliveryMethod?.Type === 'OUTSIDE_DELIVER') {
      return this.translateService.instant('DeliverToHome');
    } else if (factor?.DeliveryMethod?.Type === 'INSIDE_DELIVER') {
      return this.translateService.instant('InPersonDelivery');
    } else {
      return this.translateService.instant('Inside');
    }
  }

  getFirstOrderItemName(factor: any): string {
    if (factor?.FactorOrders && factor.FactorOrders.length > 0) {
      const firstOrder = factor.FactorOrders[0];
      if (firstOrder?.Order?.ServicePrice?.Service?.Title) {
        return firstOrder.Order.ServicePrice.Service.Title;
      }
    }
    return '';
  }

  getFirstOrderItemImage(factor: any): string {
    if (factor?.FactorOrders && factor.FactorOrders.length > 0) {
      const firstOrder = factor.FactorOrders[0];
      if (firstOrder?.Order?.ServicePrice?.Service?.Images && firstOrder.Order.ServicePrice.Service.Images.length > 0) {
        return this.formatImage('', firstOrder.Order.ServicePrice.Service.Images[0]);
      }
    }
    return '';
  }

  deliveryTime(factor: any): string {
    const timestamp = factor?.Metadata?.RemainedTimeToDeliver;
    if (!timestamp) return '-';
    const date = new Date(Number(timestamp));
    // زمان به وقت تهران (UTC +3:30)
    const utc = date.getTime() + (date.getTimezoneOffset() * 60000);
    const tehranTime = new Date(utc + (3.5 * 60 * 60 * 1000));
    const hours = tehranTime.getHours().toString().padStart(2, '0');
    const minutes = tehranTime.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  openBackClicked() {
    this.location.back();
  }

  openFactorClicked(factor: any) {
    this.matDialog.open(FactorComponent, {
      width: this.isMobile ? '100%' : '1284px',
      height: this.isMobile ? 'auto' : '60%',
      panelClass: this.isMobile ? 'mobile-dialog-panel' : 'desktop-dialog-panel',
      data: factor,
    });
  }

  reOrderClicked(factor: any) {
    const isMobile = window.innerWidth <= 992;
  }

  openRegisterCommentClicked(factorId: string) {
    this.matDialog.open(RegisterCommentComponent, {
      width: this.isMobile ? '100%' : '355px',
      height: this.isMobile ? 'auto' : '60%',
      panelClass: this.isMobile ? 'mobile-dialog-panel' : 'desktop-dialog-panel',
      data: {
        FactorId: factorId,
      },
    });
  }

  openCardToCardClicked(factor: any) {
    if (factor?.PaymentMethod?.Type === 'CARD_TO_CARD') {
      this.router.navigate([RouteUtil.CLIENT_MODULE, RouteUtil.PAYMENT_DETAIL_COMPONENT, Config.branch.UniqueIdentifier, {
        FactorId: factor.FactorId
      }]).then();
    } else {
      this.router.navigate([RouteUtil.CLIENT_MODULE, RouteUtil.PAYMENT_METHODS_COMPONENT, Config.branch.UniqueIdentifier]).then();
    }
  }
}
